// Modules
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, ModuleWithProviders } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HighchartsChartModule } from 'highcharts-angular';

// Components
import { GridComponent } from './components/grid/grid.component';
import { ReportComponent } from './components/report/report.component';
import { CheckboxComponent } from './components/checkbox/checkbox.component';
import { DatePickerComponent } from './components/datepicker/datepicker.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { RadioButtonsComponent } from './components/radio-buttons/radio-buttons.component';
import { TogglerComponent } from './components/toggler/toggler.component';
import { SliderComponent } from './components/slider/slider.component';
import { HintComponent } from './components/hint/hint.component';
import { AutocompleteComponent } from './components/autocomplete/autocomplete.component';
import { FormErrorComponent } from './components/form-error/form-error.component';
import { ColorpickerComponent } from './components/colorpicker/colorpicker.component';
import { FileBrowserComponent } from './components/file-browser/file-browser.component';
import { SimpletagsComponent } from './components/simpletags/simpletags.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { JsonTextComponent } from './components/json/text/json-text.component';
import { JsonSelectComponent } from './components/json/select/json-select.component';
import { JsonEditorComponent } from './components/json/editor/json-editor.component';
import { JsonBooleanComponent } from './components/json/boolean/json-boolean.component';
import { AreaChartComponent } from './components/area-chart/area-chart.component';

// Services
import { SharedService } from './services/shared.service';
import { HttpService } from './services/http.service';
import { DictionaryService } from './services/dictionary.service';
import { GridService } from './services/grid.service';
import { GoogleAnalyticsService } from './services/google-analytics.service';
import { SupplyService } from './services/supply.service';
import { DemandService } from './services/demand.service';
import { ReportsService } from './services/reports.service';
import { ModalsService } from './services/modals.service';
import { UserService } from './services/user.service';
import { ListService } from './services/list.service';
import { QualityService } from './services/quality.service';
import { CallbacksService } from './services/callbacks.service';
import { CampaignsService } from './services/campaigns.service';
import { AutomaticReportsService } from './services/automatic-reports.service';

// Guards
import { AuthGuard } from './guards/auth.guard';
import { UserTypeGuard } from './guards/user-type.guard';
import { ChangesGuard } from './guards/changes.guard';
import { FeatureGuard } from './guards/feature.guard';

// Pipes
import { DictionaryPipe } from './pipes/dictionary.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { ToLocalePipe } from './pipes/to-locale.pipe';

// Directives
import { TooltipDirective } from './directives/tooltip.directive';
import { ToLocaleFormatterDirective } from './directives/to-locale-formatter.directive';
import { MacrosService } from './services/macros.service';

// NgModule
@NgModule({
  declarations: [
    // Components
    DatePickerComponent,
    GridComponent,
    ReportComponent,
    CheckboxComponent,
    TogglerComponent,
    RadioButtonsComponent,
    DropdownComponent,
    SliderComponent,
    HintComponent,
    AutocompleteComponent,
    FormErrorComponent,
    ColorpickerComponent,
    FileBrowserComponent,
    SimpletagsComponent,
    SpinnerComponent,
    JsonTextComponent,
    JsonSelectComponent,
    JsonEditorComponent,
    JsonBooleanComponent,
    // Pipes
    SafeHtmlPipe,
    DictionaryPipe,
    // Directives
    TooltipDirective,
    ToLocaleFormatterDirective,
    ToLocalePipe,
    AreaChartComponent,
  ],
  exports: [
    // Components
    DatePickerComponent,
    GridComponent,
    ReportComponent,
    CheckboxComponent,
    TogglerComponent,
    RadioButtonsComponent,
    DropdownComponent,
    SliderComponent,
    HintComponent,
    AutocompleteComponent,
    FormErrorComponent,
    ColorpickerComponent,
    FileBrowserComponent,
    SimpletagsComponent,
    SpinnerComponent,
    JsonTextComponent,
    JsonSelectComponent,
    JsonEditorComponent,
    JsonBooleanComponent,
    // Pipes
    SafeHtmlPipe,
    DictionaryPipe,
    // Directives
    TooltipDirective,
    ToLocaleFormatterDirective,
    ToLocalePipe,
    AreaChartComponent,
  ],
  imports: [CommonModule, FormsModule, RouterModule, HighchartsChartModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {
  static forRoot(): ModuleWithProviders {
    return {
      ngModule: SharedModule,
      providers: [
        // Services
        SharedService,
        HttpService,
        DictionaryService,
        GridService,
        GoogleAnalyticsService,
        SupplyService,
        DemandService,
        MacrosService,
        ReportsService,
        ModalsService,
        UserService,
        ListService,
        QualityService,
        CallbacksService,
        CampaignsService,
        AutomaticReportsService,
        // Guards
        AuthGuard,
        UserTypeGuard,
        ChangesGuard,
        FeatureGuard,
        ToLocaleFormatterDirective,
        ToLocalePipe,
      ],
    };
  }
}
